import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, signal, OnInit, OnDestroy, effect } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { CartService } from '@b2b/features/cart/core/cart.service';
import { CartItem } from '@b2b/features/cart/utilities/cart.model';
import { Product, PRODUCT_MEDIA_TYPE } from '@b2b/features/product/core/utilities/product.model';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription, take } from 'rxjs';
import { ProductImageService } from '@b2b/features/product/core/services/product-image.service';
import { ProductCardCutOffComponent } from '@b2b/components/cut-off-time/cut-off-time.component';
import { InputQuantityComponent } from '@shared/components/form-elements/input-quantity/input-quantity.component';
import { MediaComponent } from '@shared/components/media/media.component';
import { ProductStockComponent } from '@b2b/components/product/product-stock/product-stock.component';
import { TOASTER_TYPE } from '@shared/components/toaster/toaster.model';
import { ToasterService } from '@shared/components/toaster/toaster.service';
import { ProductConnector } from '@b2b/features/product/core/services/product.connector';
import { SkeletonLoadingComponent } from '@shared/components/skeleton-loading/skeleton-loading.component';
import { ProductListService } from '@b2b/features/product/core/services/product-list.service';
import { ProductSearchService } from '@b2b/features/product/core/services/product-lookup.service';
import { ButtonComponent } from '@shared/components/button/button.component';

@Component({
  selector: 'product-card',
  templateUrl: './product-card.component.html',
  styleUrl: './product-card.component.scss',
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    InputQuantityComponent,
    ReactiveFormsModule,
    ProductCardCutOffComponent,
    ProductStockComponent,
    MediaComponent,
    SkeletonLoadingComponent,
    ButtonComponent,
  ],
})
export class ProductCardComponent implements OnInit, OnDestroy {
  private readonly productImageService = inject(ProductImageService);
  private readonly cartService = inject(CartService);
  private readonly toasterService = inject(ToasterService);
  private readonly productConnector = inject(ProductConnector);
  private readonly productListService = inject(ProductListService);
  private readonly productSearchService = inject(ProductSearchService);

  private cart = toSignal(this.cartService.getCart());

  // Inputs
  product = input.required<Product>();
  index = input<number>();
  hoverable = input(true);
  isCarousel = input(false);

  isStockLoading = signal(true);
  isPriceLoading = signal(true);
  productComputed = signal<Product | undefined>(undefined);

  showStrikethrough = computed(() => {
    const pricing = this.productComputed()?.price?.data;
    if (!pricing) return false;
    return pricing.finalPrice < pricing.price;
  });

  priceData = computed(() => {
    const product = this.productComputed();
    return product?.price?.data || null;
  });

  isAddCartLoading = false;
  imageNotFound = this.productImageService.getImageNotFound(PRODUCT_MEDIA_TYPE.PRODUCT);
  qtyControl = new FormControl(1);

  priceSub: Subscription | undefined = undefined;
  stockSub: Subscription | undefined = undefined;

  imageProduct = computed(() => {
    const product = this.product();

    if (!product) return this.imageNotFound;
    return this.productImageService.getImage(product, PRODUCT_MEDIA_TYPE.PRODUCT);
  });

  constructor() {
    effect(() => {
      if (!this.isCarousel()) return;

      const cache = this.productSearchService.fetchedSearchProducts();
      const productOnLookup = cache[this.product().code!];

      if (productOnLookup && productOnLookup.stock?.data && productOnLookup.price?.data) {
        this.productComputed.set(productOnLookup);
        this.productSearchService.isLoadingSearchIds[productOnLookup.code!] = false;
        this.isStockLoading.set(false);
        this.isPriceLoading.set(false);

        return;
      }
    });
  }

  ngOnInit(): void {
    const input = this.product();
    this.productComputed.set(input);

    if (this.isCarousel()) {
      if (!this.productSearchService.isLoadingSearchIds[input.code!]) {
        this.productSearchService.isLoadingSearchIds[input.code!] = true;
        this.loadProduct(input);
      }

      return;
    }

    this.loadProduct(input);
  }

  ngOnDestroy(): void {
    this.priceSub?.unsubscribe();
    this.stockSub?.unsubscribe();
  }

  private loadProduct(product: Product): void {
    this.priceSub?.unsubscribe();
    this.stockSub?.unsubscribe();

    if (!product?.stock) {
      this.fetchStock();
    } else {
      this.isStockLoading.set(false);
    }

    if (!product?.price) {
      this.fetchPrice();
    } else {
      this.isPriceLoading.set(false);
    }
  }

  private fetchPrice() {
    this.isPriceLoading.set(true);

    this.priceSub = this.productConnector
      .getPrice(this.product().code!)
      .pipe(take(1))
      .subscribe(price => {
        const currentProduct = this.productComputed()!;

        const prodWithPrice = {
          ...currentProduct,
          price: price,
        };

        this.productComputed.update(currentProduct => {
          if (!currentProduct) {
            return prodWithPrice;
          }

          return {
            ...currentProduct,
            ...prodWithPrice,
            stock: currentProduct.stock ?? prodWithPrice.stock,
          };
        });

        const updatedProduct = this.productComputed();

        if (this.isCarousel()) {
          this.productSearchService.fetchedSearchProducts.update(cache => ({
            ...cache,
            [prodWithPrice.code!]: updatedProduct,
          }));
        }

        const index = this.index();
        if (index !== undefined && updatedProduct) {
          this.productListService.setSpecificProduct(updatedProduct, index);
        }

        this.isPriceLoading.set(false);
      });
  }

  private fetchStock() {
    this.isStockLoading.set(false);

    this.stockSub = this.productConnector
      .getStock(this.product().code!)
      .pipe(take(1))
      .subscribe(stock => {
        const currentProduct = this.productComputed()!;

        const prodWithStock = {
          ...currentProduct,
          stock: stock,
        };

        this.productComputed.set(prodWithStock);

        if (this.isCarousel()) {
          this.productSearchService.fetchedSearchProducts.update(cache => ({
            ...cache,
            [prodWithStock.code!]: prodWithStock,
          }));
        }

        const index = this.index();
        if (index !== undefined) {
          this.productListService.setSpecificProduct(prodWithStock, index);
        }
        this.isStockLoading.set(false);
      });
  }

  onImageError(event: Event): void {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = this.imageNotFound.src;
  }

  addToCart(): void {
    const product = this.productComputed();
    const qty = Number(this.qtyControl.value);

    if (!product) return;

    const item: CartItem = {
      warehouse: product.stock?.data?.cdID || '',
      productCode: product.code || '',
      qty,
    };

    this.isAddCartLoading = true;
    this.cartService
      .addCartItem(item)
      .pipe(take(1))
      .subscribe(response => {
        if (!response.hasError) {
          this.qtyControl.setValue(1);

          this.toasterService.init({
            id: 'toaster-add-to-cart',
            title: 'Produto Adicionado no Carrinho!',
            type: TOASTER_TYPE.UPDATE_CART_ITEM,
            content: '',
            cartItem: {
              cartCode: this.cartService.getCartID(),
              product: product,
            },
          });
        } else {
          this.toasterService.init({
            id: 'toaster-add-to-cart-fail',
            title: response.message,
            type: TOASTER_TYPE.DANGER,
            content: '',
            cartItem: {
              cartCode: this.cartService.getCartID(),
              product: product,
            },
          });
        }
        this.isAddCartLoading = false;
      });
  }
}
